import {useQuery} from "@tanstack/react-query";
import {qk} from "@queries/keys";
import CatalogService from "@lib/Catalog/CatalogService";
import {mapCatalogProductToCard, mapCatalogProductToProductPage} from "@lib/Catalog/CatalogMappers";
import {useEffect, useMemo, useState} from "react";
import {ceilToStep, floorToStep} from "@commons/Utils";
import {ProductCardVm, ProductVm} from "@lib/Catalog/CatalogModels";

export function useCatalog(size: number = 250) {
    return useQuery({
        queryKey: qk.products(size),
        queryFn: () => CatalogService.getCatalog(size),
        staleTime: 10 * 60_000,
        retry: 1,
        select: (res): ProductCardVm[] => {
            return (res.results ?? []).map(p => mapCatalogProductToCard(p));
        },
    });
}

export function useProduct(productId?: string) {
    return useQuery({
        queryKey: qk.product(productId),
        queryFn: () => {
            if (!productId) throw new Error('Missing productId');
            return CatalogService.getProduct(productId);
        },
        staleTime: 10 * 60_000,
        retry: 1,
        enabled: !!productId,
        select: (res): ProductVm => mapCatalogProductToProductPage(res),
    });
}

export function useCart() {
    return useQuery({
        queryKey: qk.cart(),
        queryFn: () => CatalogService.getCart(),
        retry: 1,
    });
}

export type ProductFiltersState = {
    q: string;
    brand: string;
    category: string;
    minPoints: number;
    maxPoints: number;
};

function uniqSorted(values: string[]) {
    return Array.from(new Set(values.map(v => v.trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
}

export function useProductFilters(products: ProductCardVm[]) {
    const brands = useMemo(() => uniqSorted(products.map(p => p.brand)), [products]);
    const categories = useMemo(() => uniqSorted(products.map(p => p.category)), [products]);

    const {minAll, maxAll} = useMemo(() => {
        if (!products.length) return {minAll: 0, maxAll: 0};
        let min = Number.POSITIVE_INFINITY;
        let max = 0;
        for (const p of products) {
            if (p.points < min) min = p.points;
            if (p.points > max) max = p.points;
        }
        return {minAll: Number.isFinite(min) ? min : 0, maxAll: max};
    }, [products]);

    const {minBound, maxBound} = useMemo(() => {
        const baseStep = 500;
        return {
            minBound: floorToStep(minAll, baseStep),
            maxBound: ceilToStep(maxAll, baseStep),
        };
    }, [minAll, maxAll]);

    const [state, setState] = useState<ProductFiltersState>({
        q: "",
        brand: "",
        category: "",
        minPoints: 0,
        maxPoints: 0,
    });

    useEffect(() => {
        if (!products.length) return;

        setState((s) => {
            const minIsInit = !Number.isFinite(s.minPoints) || s.minPoints === 0;
            const maxIsInit = !Number.isFinite(s.maxPoints) || s.maxPoints === 0;

            if (!minIsInit && !maxIsInit) return s;

            return {
                ...s,
                minPoints: minIsInit ? minBound : s.minPoints,
                maxPoints: maxIsInit ? maxBound : s.maxPoints,
            };
        });
    }, [products.length, minBound, maxBound]);

    const filtered = useMemo(() => {
        const q = state.q.trim().toLowerCase();

        const minP = Math.max(minBound, Math.min(state.minPoints, maxBound));
        const maxP = Math.max(minBound, Math.min(state.maxPoints, maxBound));

        return products.filter(p => {
            if (state.brand && p.brand !== state.brand) return false;
            if (state.category && p.category !== state.category) return false;
            if (p.points < minP) return false;
            if (p.points > maxP) return false;
            return !(q && !p.title.toLowerCase().includes(q));
        });
    }, [products, state, minBound, maxBound]);

    const hasActiveFilters = useMemo(() => {
        if (!products.length) return false;
        return (
            state.q.trim() !== "" ||
            state.brand !== "" ||
            state.category !== "" ||
            state.minPoints !== minBound ||
            state.maxPoints !== maxBound
        );
    }, [state, minBound, maxBound, products.length]);

    const reset = () => setState({q: "", brand: "", category: "", minPoints: minBound, maxPoints: maxBound});

    return {state, setState, filtered, brands, categories, minAll, maxAll, minBound, maxBound, hasActiveFilters, reset};
}